// src/pages/DriverManagement.js - UPDATED FOR SEPARATE PICKUP/DELIVERY LINKS
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  onSnapshot,
  query,
  orderBy,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { sendDriverAssignment, notifyAdminDriverAssignment } from '../services/emailService';

const DriverManagement = () => {
  const [drivers, setDrivers] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigningDriver, setAssigningDriver] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState('');
  const [notificationMethod, setNotificationMethod] = useState('email'); // 'email', 'sms', or 'both'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    licenseNumber: '',
    vehicleInfo: ''
  });

  useEffect(() => {
    // Listen to drivers
    const driversQuery = query(collection(db, 'drivers'), orderBy('name'));
    const unsubscribeDrivers = onSnapshot(driversQuery, (querySnapshot) => {
      const driversData = [];
      querySnapshot.forEach((doc) => {
        driversData.push({ id: doc.id, ...doc.data() });
      });
      setDrivers(driversData);
    });

    // Listen to shipments that are ready for drivers (confirmed or in transit)
    const shipmentsQuery = query(
      collection(db, 'shipments'),
      where('status', 'in', ['confirmed', 'in_transit']),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribeShipments = onSnapshot(shipmentsQuery, (querySnapshot) => {
      const shipmentsData = [];
      querySnapshot.forEach((doc) => {
        shipmentsData.push({ 
          id: doc.id, 
          ...doc.data(),
          displayId: doc.data().displayId || doc.id.substring(0, 8).toUpperCase()
        });
      });
      setShipments(shipmentsData);
    });

    return () => {
      unsubscribeDrivers();
      unsubscribeShipments();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await addDoc(collection(db, 'drivers'), {
        ...formData,
        createdAt: serverTimestamp(),
        status: 'active',
        assignedShipments: []
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        licenseNumber: '',
        vehicleInfo: ''
      });
      
      alert('Driver added successfully!');
    } catch (error) {
      console.error('Error adding driver:', error);
      alert('Error adding driver');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (driverId) => {
    if (window.confirm('Are you sure you want to delete this driver?')) {
      try {
        await deleteDoc(doc(db, 'drivers', driverId));
        alert('Driver deleted successfully!');
      } catch (error) {
        console.error('Error deleting driver:', error);
        alert('Error deleting driver');
      }
    }
  };

  const handleStatusUpdate = async (driverId, newStatus) => {
    try {
      await updateDoc(doc(db, 'drivers', driverId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      alert(`Driver status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating driver status:', error);
      alert('Error updating driver status');
    }
  };

  // ✅ UPDATED: Assign shipment to driver with separate pickup/delivery links
  const handleAssignShipment = async (driverId) => {
    if (!selectedShipment) {
      alert('Please select a shipment first');
      return;
    }

    setAssigningDriver(driverId);

    try {
      const driver = drivers.find(d => d.id === driverId);
      const shipment = shipments.find(s => s.id === selectedShipment);

      if (!driver || !shipment) {
        alert('Driver or shipment not found');
        return;
      }

      // Generate unique tokens for pickup and delivery
      const pickupToken = `pickup_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const deliveryToken = `delivery_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const baseUrl = window.location.origin;
      const pickupLink = `${baseUrl}/driver-signature/${shipment.id}?token=${pickupToken}&driverId=${driverId}&step=pickup`;
      const deliveryLink = `${baseUrl}/driver-signature/${shipment.id}?token=${deliveryToken}&driverId=${driverId}&step=delivery`;

      // Update shipment with driver assignment and separate tokens
      await updateDoc(doc(db, 'shipments', shipment.id), {
        assignedDriver: {
          driverId: driverId,
          driverName: driver.name,
          driverEmail: driver.email,
          driverPhone: driver.phone,
          vehicleInfo: driver.vehicleInfo,
          assignedAt: serverTimestamp(),
          pickupToken: pickupToken,
          deliveryToken: deliveryToken,
          pickupLink: pickupLink,
          deliveryLink: deliveryLink,
          status: 'assigned',
          notificationMethod: notificationMethod
        },
        updatedAt: serverTimestamp()
      });

      // Update driver's assigned shipments
      const currentAssignments = driver.assignedShipments || [];
      await updateDoc(doc(db, 'drivers', driverId), {
        assignedShipments: [...currentAssignments, {
          shipmentId: shipment.id,
          shipmentDisplayId: shipment.displayId,
          assignedAt: new Date(),
          status: 'assigned',
          pickupLink: pickupLink,
          deliveryLink: deliveryLink,
          notificationMethod: notificationMethod
        }],
        lastAssignment: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // ✅ UPDATED: Send notification to driver with both links
      const notificationResult = await sendDriverAssignment(
        driver, 
        shipment, 
        { pickupLink, deliveryLink }, 
        notificationMethod
      );
      
      // Notify admin
      await notifyAdminDriverAssignment(driver, shipment);

      // Show appropriate success message
      let successMessage = `✅ Shipment assigned to ${driver.name}! `;
      
      if (notificationMethod === 'email') {
        successMessage += 'Email notification sent with both pickup and delivery links.';
      } else if (notificationMethod === 'sms') {
        successMessage += 'SMS notification sent. Links available in admin dashboard.';
      } else if (notificationMethod === 'both') {
        successMessage += 'Email and SMS notifications sent.';
      }
      
      // Add information about separate links
      successMessage += '\n\n📝 Use Admin Dashboard to send specific links when driver is at each location.';
      
      // Add warnings if any method failed
      if (notificationMethod.includes('email') && !notificationResult.emailSent) {
        successMessage += ' (Email failed - check console)';
      }
      
      if (notificationMethod.includes('sms') && !notificationResult.smsSent) {
        successMessage += ' (SMS failed - check console)';
      }

      alert(successMessage);
      setSelectedShipment(''); // Reset selection

    } catch (error) {
      console.error('Error assigning shipment:', error);
      alert('Error assigning shipment to driver');
    } finally {
      setAssigningDriver(null);
    }
  };

  // ✅ NEW: Send specific link to driver (pickup or delivery)
  const sendDriverLink = async (driverId, shipmentId, step) => {
    try {
      const driver = drivers.find(d => d.id === driverId);
      const shipment = shipments.find(s => s.id === shipmentId);
      
      if (!driver || !shipment) {
        alert('Driver or shipment not found');
        return;
      }

      const link = step === 'pickup' 
        ? shipment.assignedDriver?.pickupLink
        : shipment.assignedDriver?.deliveryLink;

      if (!link) {
        alert(`${step} link not available. Please reassign the shipment.`);
        return;
      }

      // Copy to clipboard
      navigator.clipboard.writeText(link).then(() => {
        alert(`✅ ${step.charAt(0).toUpperCase() + step.slice(1)} link copied to clipboard! Send this to ${driver.name} when they are at the ${step} location.`);
      }).catch(() => {
        alert(`✅ ${step.charAt(0).toUpperCase() + step.slice(1)} link for ${driver.name}:\n\n${link}`);
      });

    } catch (error) {
      console.error(`Error sending ${step} link:`, error);
      alert(`Error sending ${step} link`);
    }
  };

  // View driver's current assignments with link management
  const viewDriverAssignments = (driver) => {
    const assignments = driver.assignedShipments || [];
    if (assignments.length === 0) {
      alert('No shipments assigned to this driver');
      return;
    }

    let assignmentText = `Current assignments for ${driver.name}:\n\n`;
    
    assignments.forEach((assignment, index) => {
      const shipment = shipments.find(s => s.id === assignment.shipmentId);
      const status = shipment?.status || 'Unknown';
      
      assignmentText += `${index + 1}. ${assignment.shipmentDisplayId} - ${status}\n`;
      assignmentText += `   📍 Pickup: ${shipment?.pickupAddress?.split('\n')[0]}\n`;
      assignmentText += `   🏠 Delivery: ${shipment?.dropoffAddress?.split('\n')[0]}\n`;
      assignmentText += `   🔗 Links available in Admin Dashboard\n\n`;
    });

    assignmentText += "💡 Use Admin Dashboard to send specific pickup/delivery links when driver is at each location.";

    alert(assignmentText);
  };

  // Unassign shipment from driver
  const handleUnassignShipment = async (driverId, shipmentId) => {
    if (window.confirm('Are you sure you want to unassign this shipment?')) {
      try {
        const driver = drivers.find(d => d.id === driverId);
        const updatedAssignments = (driver.assignedShipments || []).filter(
          assignment => assignment.shipmentId !== shipmentId
        );

        await updateDoc(doc(db, 'drivers', driverId), {
          assignedShipments: updatedAssignments,
          updatedAt: serverTimestamp()
        });

        // Also remove driver assignment from shipment
        await updateDoc(doc(db, 'shipments', shipmentId), {
          assignedDriver: null,
          updatedAt: serverTimestamp()
        });

        alert('Shipment unassigned successfully!');
      } catch (error) {
        console.error('Error unassigning shipment:', error);
        alert('Error unassigning shipment');
      }
    }
  };

  const copySignatureLink = (link) => {
    navigator.clipboard.writeText(link).then(() => {
      alert('Signature link copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy link. Please copy it manually.');
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Driver Management</h1>
          <p className="text-gray-600 mt-2">Manage drivers and assign shipments for signature capture</p>
          {/* ✅ NEW: Information about separate links */}
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>New Feature:</strong> Drivers now receive separate links for pickup and delivery documentation. 
              Use Admin Dashboard to send specific links when driver is at each location.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Add Driver Form */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Add New Driver</h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Driver name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="driver@email.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">License Number *</label>
                  <input
                    type="text"
                    required
                    value={formData.licenseNumber}
                    onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="DL123456789"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Information</label>
                  <input
                    type="text"
                    value={formData.vehicleInfo}
                    onChange={(e) => setFormData({ ...formData, vehicleInfo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 2020 Ford F-150, License Plate: ABC123"
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="mt-6 w-full bg-blue-600 text-white px-4 py-3 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 font-semibold"
              >
                {loading ? 'Adding Driver...' : 'Add Driver'}
              </button>
            </form>
          </div>

          {/* Shipment Assignment Section */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Assign Shipment</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Shipment to Assign
                </label>
                <select
                  value={selectedShipment}
                  onChange={(e) => setSelectedShipment(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Choose a shipment...</option>
                  {shipments.map(shipment => (
                    <option key={shipment.id} value={shipment.id}>
                      {shipment.displayId} - {shipment.itemDescription} - {shipment.status}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedShipment 
                    ? 'Ready to assign to a driver' 
                    : 'Select a shipment to enable assignment'
                  }
                </p>
              </div>

              {/* Notification Method Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Method
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setNotificationMethod('email')}
                    className={`p-3 border rounded-lg text-center transition-colors ${
                      notificationMethod === 'email' 
                        ? 'bg-blue-100 border-blue-500 text-blue-700' 
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-lg mb-1">📧</div>
                    <div className="text-xs font-medium">Email Only</div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setNotificationMethod('sms')}
                    className={`p-3 border rounded-lg text-center transition-colors ${
                      notificationMethod === 'sms' 
                        ? 'bg-green-100 border-green-500 text-green-700' 
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-lg mb-1">📱</div>
                    <div className="text-xs font-medium">SMS Only</div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setNotificationMethod('both')}
                    className={`p-3 border rounded-lg text-center transition-colors ${
                      notificationMethod === 'both' 
                        ? 'bg-purple-100 border-purple-500 text-purple-700' 
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-lg mb-1">📧📱</div>
                    <div className="text-xs font-medium">Both</div>
                  </button>
                </div>
                
                {/* Helper text based on selection */}
                <div className="mt-2 text-xs text-gray-500">
                  {notificationMethod === 'email' && 'Driver will receive an email with shipment details and both signature links'}
                  {notificationMethod === 'sms' && 'Driver will receive an SMS with shipment details. Links available in admin dashboard.'}
                  {notificationMethod === 'both' && 'Driver will receive both email and SMS notifications'}
                </div>
              </div>

              {/* Available Drivers */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Available Drivers</h3>
                <div className="space-y-3">
                  {drivers.filter(driver => driver.status === 'active').map((driver) => (
                    <div key={driver.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-gray-900">{driver.name}</h4>
                          <p className="text-sm text-gray-600">{driver.email}</p>
                          <p className="text-sm text-gray-600">{driver.phone}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {(driver.assignedShipments || []).length} active assignments
                          </p>
                        </div>
                        <button
                          onClick={() => handleAssignShipment(driver.id)}
                          disabled={!selectedShipment || assigningDriver === driver.id}
                          className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {assigningDriver === driver.id ? 'Assigning...' : 'Assign'}
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {drivers.filter(driver => driver.status === 'active').length === 0 && (
                    <p className="text-gray-500 text-center py-4">No active drivers available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Drivers List */}
        <div className="mt-8 bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">All Drivers</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">License</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assignments</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {drivers.map((driver) => (
                  <tr key={driver.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900">{driver.name}</div>
                      <div className="text-sm text-gray-500">{driver.vehicleInfo}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      <div>{driver.email}</div>
                      <div>{driver.phone}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {driver.licenseNumber}
                    </td>
                    <td className="px-4 py-4">
                      <select
                        value={driver.status}
                        onChange={(e) => handleStatusUpdate(driver.id, e.target.value)}
                        className="text-sm border rounded px-2 py-1"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="on_leave">On Leave</option>
                      </select>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      <div className="flex flex-col gap-1">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {(driver.assignedShipments || []).length} assigned
                        </span>
                        {(driver.assignedShipments || []).length > 0 && (
                          <button
                            onClick={() => viewDriverAssignments(driver)}
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                          >
                            View Details
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm font-medium space-y-2">
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => handleDelete(driver.id)}
                          className="text-red-600 hover:text-red-900 text-xs px-3 py-1 border border-red-300 rounded"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {drivers.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No drivers added yet. Add your first driver above.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverManagement;